import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Trash2, Tag, Percent, DollarSign, Gift, Copy, Users, Package } from 'lucide-react';

const PACKAGE_OPTIONS = [
  { id: 'individual', name: 'Single Assessment', price: '$10' },
  { id: 'small_pack', name: 'Small Pack', price: '$90' },
  { id: 'medium_pack', name: 'Medium Pack', price: '$425' },
  { id: 'large_pack', name: 'Large Pack', price: '$800' }
];

const PromoCodeManager = () => {
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newCode, setNewCode] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: 0,
    description: '',
    max_uses: '',
    valid_until: '',
    applicable_packages: []
  });

  useEffect(() => {
    fetchPromoCodes();
  }, []);

  const fetchPromoCodes = async () => {
    try {
      const response = await axios.get(`${API}/admin/promo-codes`);
      // Handle both array and object response formats
      const codes = response.data.promo_codes || response.data;
      setPromoCodes(Array.isArray(codes) ? codes : []);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Only super admin can manage promo codes');
      } else {
        toast.error('Failed to load promo codes');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!newCode.code.trim()) {
      toast.error('Please enter a promo code');
      return;
    }

    try {
      const payload = {
        code: newCode.code.toUpperCase(),
        discount_type: newCode.discount_type,
        discount_value: parseFloat(newCode.discount_value) || 0,
        description: newCode.description,
        max_uses: newCode.max_uses ? parseInt(newCode.max_uses) : null,
        valid_until: newCode.valid_until || null,
        applicable_packages: newCode.applicable_packages
      };

      await axios.post(`${API}/admin/promo-codes`, payload);
      toast.success(`Promo code "${newCode.code.toUpperCase()}" created!`);
      setNewCode({
        code: '',
        discount_type: 'percentage',
        discount_value: 0,
        description: '',
        max_uses: '',
        valid_until: '',
        applicable_packages: []
      });
      setShowCreate(false);
      fetchPromoCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create promo code');
    }
  };

  const handleToggleActive = async (codeId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/promo-codes/${codeId}`, {
        is_active: !currentStatus
      });
      toast.success(currentStatus ? 'Promo code deactivated' : 'Promo code activated');
      fetchPromoCodes();
    } catch (error) {
      toast.error('Failed to update promo code');
    }
  };

  const handleDelete = async (codeId, codeName) => {
    if (!window.confirm(`Are you sure you want to delete "${codeName}"? This cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/promo-codes/${codeId}`);
      toast.success('Promo code deleted');
      fetchPromoCodes();
    } catch (error) {
      toast.error('Failed to delete promo code');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Copied "${code}" to clipboard`);
  };

  const getDiscountDisplay = (promo) => {
    if (promo.discount_type === 'free') {
      return 'FREE';
    } else if (promo.discount_type === 'percentage') {
      return `${promo.discount_value}% OFF`;
    } else {
      return `$${promo.discount_value} OFF`;
    }
  };

  const getDiscountIcon = (type) => {
    if (type === 'free') return <Gift className="h-4 w-4" />;
    if (type === 'percentage') return <Percent className="h-4 w-4" />;
    return <DollarSign className="h-4 w-4" />;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-gray-500">Loading promo codes...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Tag className="h-5 w-5" />
                Promo Codes
              </CardTitle>
              <CardDescription>
                Create and manage discount codes for your users
              </CardDescription>
            </div>
            <Button onClick={() => setShowCreate(!showCreate)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Code
            </Button>
          </div>
        </CardHeader>

        {showCreate && (
          <CardContent className="border-t bg-gray-50">
            <form onSubmit={handleCreate} className="space-y-4 pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="code">Promo Code</Label>
                  <Input
                    id="code"
                    placeholder="e.g., BUILDR50, FREEFIRST"
                    value={newCode.code}
                    onChange={(e) => setNewCode({ ...newCode, code: e.target.value.toUpperCase() })}
                    className="uppercase"
                  />
                </div>
                <div>
                  <Label htmlFor="discount_type">Discount Type</Label>
                  <Select
                    value={newCode.discount_type}
                    onValueChange={(value) => setNewCode({ ...newCode, discount_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="free">Free (100% Off)</SelectItem>
                      <SelectItem value="percentage">Percentage Off</SelectItem>
                      <SelectItem value="fixed">Fixed Amount Off</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {newCode.discount_type !== 'free' && (
                <div>
                  <Label htmlFor="discount_value">
                    {newCode.discount_type === 'percentage' ? 'Discount Percentage' : 'Discount Amount ($)'}
                  </Label>
                  <Input
                    id="discount_value"
                    type="number"
                    min="0"
                    max={newCode.discount_type === 'percentage' ? '100' : undefined}
                    placeholder={newCode.discount_type === 'percentage' ? 'e.g., 25' : 'e.g., 5'}
                    value={newCode.discount_value}
                    onChange={(e) => setNewCode({ ...newCode, discount_value: e.target.value })}
                  />
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="max_uses">Max Uses (optional)</Label>
                  <Input
                    id="max_uses"
                    type="number"
                    min="1"
                    placeholder="Leave empty for unlimited"
                    value={newCode.max_uses}
                    onChange={(e) => setNewCode({ ...newCode, max_uses: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="valid_until">Expiration Date (optional)</Label>
                  <Input
                    id="valid_until"
                    type="datetime-local"
                    value={newCode.valid_until}
                    onChange={(e) => setNewCode({ ...newCode, valid_until: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="description">Description (internal notes)</Label>
                <Input
                  id="description"
                  placeholder="e.g., Launch promotion, VIP customer code"
                  value={newCode.description}
                  onChange={(e) => setNewCode({ ...newCode, description: e.target.value })}
                />
              </div>

              <div>
                <Label className="flex items-center gap-2 mb-3">
                  <Package className="h-4 w-4" />
                  Applicable Packages
                </Label>
                <p className="text-sm text-gray-500 mb-3">
                  Select which packages this promo code can be applied to. Leave all unchecked for all packages.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {PACKAGE_OPTIONS.map((pkg) => {
                    const isSelected = newCode.applicable_packages.includes(pkg.id);
                    return (
                      <label
                        key={pkg.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          isSelected
                            ? 'bg-indigo-50 border-indigo-300'
                            : 'bg-white border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setNewCode({
                                ...newCode,
                                applicable_packages: [...newCode.applicable_packages, pkg.id]
                              });
                            } else {
                              setNewCode({
                                ...newCode,
                                applicable_packages: newCode.applicable_packages.filter(p => p !== pkg.id)
                              });
                            }
                          }}
                        />
                        <div>
                          <div className="font-medium text-sm">{pkg.name}</div>
                          <div className="text-xs text-gray-500">{pkg.price}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
                {newCode.applicable_packages.length === 0 && (
                  <p className="text-xs text-indigo-600 mt-2">
                    ✓ Code will apply to all packages
                  </p>
                )}
              </div>

              <div className="flex gap-2">
                <Button type="submit">Create Promo Code</Button>
                <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        )}

        <CardContent>
          {promoCodes.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Tag className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No promo codes yet</p>
              <p className="text-sm">Create your first promo code to get started</p>
            </div>
          ) : (
            <div className="space-y-3">
              {promoCodes.map((promo) => (
                <div
                  key={promo.id}
                  className={`flex items-center justify-between p-4 rounded-lg border ${
                    promo.is_active ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-200 opacity-60'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-full ${
                      promo.discount_type === 'free' ? 'bg-green-100 text-green-600' :
                      promo.discount_type === 'percentage' ? 'bg-blue-100 text-blue-600' :
                      'bg-purple-100 text-purple-600'
                    }`}>
                      {getDiscountIcon(promo.discount_type)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-lg">{promo.code}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => copyCode(promo.code)}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                        <Badge variant={promo.is_active ? 'default' : 'secondary'}>
                          {promo.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                        <Badge variant="outline" className="font-semibold">
                          {getDiscountDisplay(promo)}
                        </Badge>
                      </div>
                      <div className="text-sm text-gray-500 mt-1 flex flex-wrap items-center gap-4">
                        {promo.description && <span>{promo.description}</span>}
                        <span className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {promo.current_uses || 0}{promo.max_uses ? `/${promo.max_uses}` : ''} uses
                        </span>
                        {promo.valid_until && (
                          <span>
                            Expires: {new Date(promo.valid_until).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                      {/* Show applicable packages */}
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <Package className="h-3 w-3 text-gray-400" />
                        {(!promo.applicable_packages || promo.applicable_packages.length === 0) ? (
                          <span className="text-xs text-green-600 font-medium">All packages</span>
                        ) : (
                          promo.applicable_packages.map(pkgId => {
                            const pkg = PACKAGE_OPTIONS.find(p => p.id === pkgId);
                            return pkg ? (
                              <Badge key={pkgId} variant="secondary" className="text-xs">
                                {pkg.name}
                              </Badge>
                            ) : null;
                          })
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Label htmlFor={`active-${promo.id}`} className="text-sm text-gray-500">
                        {promo.is_active ? 'Active' : 'Inactive'}
                      </Label>
                      <Switch
                        id={`active-${promo.id}`}
                        checked={promo.is_active}
                        onCheckedChange={() => handleToggleActive(promo.id, promo.is_active)}
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDelete(promo.id, promo.code)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PromoCodeManager;
